import { createAsyncThunk, createAction } from '@reduxjs/toolkit';
import axios from 'axios';
import { AskNovoAiDrawerState } from 'institutions/components/ask-novoai/helpers';
import {
  AdditionalResource,
  HelpOptionsSettingsResponse,
  IHelpOptionsSettingsPayload,
} from 'redux/schemas/models/help-options';
import { FileProps } from 'shared/components/nv-uploaded-file/nv-inline-uploaded-file';

export const getHelpOptionsSettings = createAsyncThunk(
  'GET_HELP_OPTIONS_SETTINGS',
  (catalogId: string) => axios
    .get<HelpOptionsSettingsResponse>(
    `/${catalogId}/learning_assistants.json`,
  )
    .then((response) => response.data.result)
    .catch(() => ({})),
);

interface SaveHelpOptionsSettingsPayload {
  catalogId: string;
  payload: IHelpOptionsSettingsPayload;
}

export const saveHelpOptionsSettings = createAsyncThunk(
  'SAVE_HELP_OPTIONS_SETTINGS',
  async ({ catalogId, payload }: SaveHelpOptionsSettingsPayload, thunkAPI) => {
    const response = await axios
      .post(`/${catalogId}/learning_assistants.json`, payload)
      .then((res) => res.data.result);
    thunkAPI.dispatch(getHelpOptionsSettings(catalogId));
    thunkAPI.dispatch(resetState());
    return response;
  },
);

export interface RecentQuestionsResponse {
  questionText: string;
  id: number;
}

export const getRecentQuestions = createAsyncThunk(
  'GET_RECENT_QUESTIONS',
  (catalogId: string) => axios
    .get<{ result: RecentQuestionsResponse[] }>(
    `/${catalogId}/user_qnas.json`,
  )
    .then((response) => response.data.result)
    .catch(() => []),
);

interface AskNovoAiPayload {
  catalogId: string;
  questionText: string;
}
interface LiveEventsResponse {
  lecturePageId: number;
  id: number;
  title: string;
  startTime: string;
  endTime: string;
}

interface ReferencedComponentsResponse {
  id: number;
  type: string;
  index: number;
  lecturePage: {
    id: number;
    title: string;
  };
}

interface ExternalReferenceResponse {
  title: string;
  url: string;
}

export interface AskNovoAiQuestionResponse {
  id?: number;
  questionText: string;
  answer: string;
  certificateUrl?: string;
  responseCode: number;
  liveEvents: LiveEventsResponse[];
  referencedComponents: ReferencedComponentsResponse[];
  externalReferences?: ExternalReferenceResponse[];
}

export const askNovoAiRequest = createAsyncThunk(
  'ASK_NOVO_AI',
  async ({ catalogId, questionText }: AskNovoAiPayload) => {
    const response = await axios
      .post<{ result: AskNovoAiQuestionResponse }>(
      `/${catalogId}/user_qnas/ask_qna_bot.json?question_text=${encodeURIComponent(
        questionText,
      )}`,
    )
      .then((res) => res.data.result);
    return response;
  },
);
export enum VoteTypes {
  UPVOTE = 'vote_up',
  DOWNVOTE = 'vote_down',
}
interface VoteNovoAiAnswerPayload {
  catalogId: string;
  qnaId: number;
  vote: VoteTypes;
}

export const voteNovoAiAnswer = createAsyncThunk(
  'VOTE_NOVO_AI',
  async ({ catalogId, qnaId, vote }: VoteNovoAiAnswerPayload) => {
    const response = await axios
      .post(`/${catalogId}/user_qnas/${qnaId}/${vote}.json`)
      .then((res) => res.data);
    return response;
  },
);

export const openNovoAIDialog = createAction('HELP_OPTIONS_OPEN_NOVOAI');
export const closeNovoAIDialog = createAction('HELP_OPTIONS_CLOSE_NOVOAI');
export const changeDrawerState = createAction(
  'CHANGE_NOVOAI_DRAWER_STATE',
  (state: AskNovoAiDrawerState) => ({
    payload: state,
  }),
);

// Vote state actions
export const setVoteLoading = createAction(
  'SET_VOTE_LOADING',
  (questionId: number, isLoading: boolean) => ({
    payload: { questionId, isLoading },
  }),
);

export const setVoteState = createAction(
  'SET_VOTE_STATE',
  (questionId: number, voteType: VoteTypes, voted: boolean) => ({
    payload: { questionId, voteType, voted },
  }),
);

export const showVoteFeedback = createAction(
  'SHOW_VOTE_FEEDBACK',
  (questionId: number, show: boolean) => ({
    payload: { questionId, show },
  }),
);

export const resetVoteState = createAction(
  'RESET_VOTE_STATE',
  (questionId: number) => ({
    payload: { questionId },
  }),
);

export const resetState = createAsyncThunk(
  'RESET_HELP_OPTIONS_STATE',
  async () => ({}),
);

export interface UploadAdditionalResourceResponse {
  fileName: string;
  url: string;
  uniqueId: string;
  fileSize: number;
  contentType: string;
  resourceId: number;
  valid: boolean;
  errors?: string;
}

export interface UploadAdditionalResourcesResponse {
  result: UploadAdditionalResourceResponse[];
  message: string;
}

interface UploadAdditionalResourcesPayload {
  catalogId: string;
  additionalResources: FileProps[];
}

export const uploadAdditionalResources = createAsyncThunk(
  'UPLOAD_ADDITIONAL_RESOURCES',
  async ({ catalogId, additionalResources }: UploadAdditionalResourcesPayload) => {
    const response = await axios
      .post<UploadAdditionalResourcesResponse>(`/${catalogId}/learning_assistant_additional_resources/upload.json`, {
      additional_resources: additionalResources,
    })
      .then((res) => res.data.result);
    return response;
  },
);

export const addAdditionalResourceFiles = createAction(
  'ADD_ADDITIONAL_RESOURCE_FILES',
  (files: File[]) => ({
    payload: { files },
  }),
);

export const updateAdditionalResource = createAction(
  'UPDATE_ADDITIONAL_RESOURCE',
  (uniqueId: string, updates: Partial<AdditionalResource>) => ({
    payload: { uniqueId, updates },
  }),
);

export const removeAdditionalResource = createAction(
  'REMOVE_ADDITIONAL_RESOURCE',
  (id: number) => ({
    payload: { id },
  }),
);
